import { TaskTemplateMatrixRowState, TaskTemplateMatrixState } from '@/redux';
import { Table } from '@/components';
import { useImportTaskTemplateColumns } from '@/views/Tasks.Details/ImportTasks/useImportTaskTemplateColumns';
import { useProjectContext } from '@/views';
import { Key, useEffect, useMemo, useRef, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import styles from './ImportTask.module.scss';

interface Props {
  name: string;
  data: TaskTemplateMatrixState[];
  isFetching: boolean;
}

export interface FlatRow extends Partial<TaskTemplateMatrixRowState> {
  isHeader?: boolean;
  templateName?: string;
  flatIndexInRows?: number;
  key: string;
  children?: FlatRow[];
}

export function ImportTasksListPanel(props: Props) {
  const { data, isFetching, name } = props;
  const { communityId, projectId } = useProjectContext();
  const columns = useImportTaskTemplateColumns(communityId, projectId);
  const { setFieldValue } = useFormikContext();
  const [{ value }] = useField<TaskTemplateMatrixRowState[]>('rows');
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([]);

  const groupedData = useMemo(() => {
    const flatRows: FlatRow[] = [];
    let globalIndex = 0;

    return data.map((template, templateIndex) => {
      const children: FlatRow[] = template.rows.map((row) => {
        const flatRow: FlatRow = {
          ...row,
          templateName: template.name,
          key: `row-${globalIndex}`,
          flatIndexInRows: globalIndex,
        };
        flatRows.push(flatRow);
        globalIndex++;
        return flatRow;
      });

      return {
        key: `template-${templateIndex}`,
        templateName: template.name,
        isHeader: true,
        children,
      } as FlatRow;
    });
  }, [data]);

  useEffect(() => {
    const selectedFlatIndexes = selectedRowKeys
      .filter((key) => key.toString().startsWith('row-'))
      .map((key) => {
        const match = key.toString().match(/-(\d+)$/);
        return match ? parseInt(match[1], 10) : null;
      })
      .filter((x): x is number => x !== null);

    setFieldValue(
      'rows',
      value.map((x, index) => ({
        ...x,
        checked: selectedFlatIndexes.includes(index),
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowKeys]);

  const didInit = useRef(false);
  useEffect(() => {
    if (!didInit.current && !isFetching && data.length > 0) {
      const allKeys: string[] = [];

      groupedData.forEach((group) => {
        allKeys.push(group.key);
        group.children?.forEach((child) => {
          allKeys.push(child.key);
        });
      });

      setSelectedRowKeys(allKeys);
      didInit.current = true;
    }
  }, [data, isFetching, groupedData]);

  return (
    <Table
      dataSource={groupedData}
      columns={columns}
      rowKey="key"
      pagination={false}
      scroll={{ x: 1700, y: 350 }}
      loading={isFetching}
      expandable={{
        expandIconColumnIndex: 2,
        expandedRowKeys,
        onExpand: (expanded, record) => {
          setExpandedRowKeys((prev) =>
            expanded
              ? [...prev, record.key]
              : prev.filter((key) => key !== record.key),
          );
        },
      }}
      rowSelection={{
        type: 'checkbox',
        selectedRowKeys,
        onChange: (newSelectedRowKeys) => {
          const newSet = new Set<Key>(newSelectedRowKeys);

          const lastChangedKey =
            selectedRowKeys.length < newSelectedRowKeys.length
              ? newSelectedRowKeys.find((k) => !selectedRowKeys.includes(k))
              : selectedRowKeys.find((k) => !newSelectedRowKeys.includes(k));

          const changedGroup = groupedData.find(
            (g) => g.key === lastChangedKey,
          );

          if (changedGroup) {
            const childKeys = changedGroup.children?.map((c) => c.key) || [];
            if (newSet.has(changedGroup.key)) {
              childKeys.forEach((key) => newSet.add(key));
            } else {
              childKeys.forEach((key) => newSet.delete(key));
            }
          }

          setSelectedRowKeys(Array.from(newSet));
        },
        getCheckboxProps: (record) => {
          const children = record.children || [];

          const total = children.length;
          const selected = children.filter((c) =>
            selectedRowKeys.includes(c.key),
          ).length;

          return {
            disabled: record.isHeader && total === 0,
            indeterminate: record.isHeader && selected > 0 && selected < total,
          };
        },
      }}
      className={styles.table}
      name={name}
      hideDefaultActions
    />
  );
}
